import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PublicNav from "../components/PublicNav";
import {
  Sparkles,
  Mail,
  MessageSquare,
  MapPin,
  Send,
  CheckCircle,
} from "lucide-react";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setError(result.error || "Failed to send message. Please try again.");
      }
    } catch (err) {
      console.error("Contact form error:", err);
      setError("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: "Email",
      value: "sales@botibonga.com",
      description: "We'll respond within 24 hours",
    },
    {
      icon: MessageSquare,
      title: "WhatsApp",
      value: "+254 717 811 921",
      description: "Chat with us directly",
    },
    {
      icon: MapPin,
      title: "Location",
      value: "Nairobi, Kenya",
      description: "Serving businesses across Africa",
    },
  ];

  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with Botibonga. We're here to help you build AI-powered WhatsApp chatbots for your business. Email, WhatsApp, or fill out our contact form."
        path="/contact"
      />

      <div
        className="min-h-screen"
        style={{
          background:
            "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)",
        }}
      >
        <PublicNav />

        {/* Hero */}
        <section className="px-6 py-16 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6"
              style={{
                background:
                  "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)",
                border: "1px solid rgba(99, 102, 241, 0.3)",
              }}
            >
              <Sparkles className="w-3.5 h-3.5" style={{ color: "#6366f1" }} />
              <span style={{ color: "#6366f1" }}>Get in Touch</span>
            </div>

            <h1
              className="text-3xl lg:text-5xl font-bold mb-6"
              style={{ color: "#1e293b" }}
            >
              We'd love to{" "}
              <span
                style={{
                  background:
                    "linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                hear from you
              </span>
            </h1>

            <p className="text-lg" style={{ color: "#64748b" }}>
              Have questions about Botibonga? Want to learn how we can help your
              business? Reach out and we'll get back to you as soon as possible.
            </p>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="px-6 pb-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {contactMethods.map((method, index) => (
                <div
                  key={index}
                  className="p-5 rounded-xl text-center"
                  style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)",
                    }}
                  >
                    <method.icon
                      className="w-5 h-5"
                      style={{ color: "#6366f1" }}
                    />
                  </div>
                  <h3
                    className="font-semibold mb-1"
                    style={{ color: "#1e293b" }}
                  >
                    {method.title}
                  </h3>
                  <p className="font-medium mb-1" style={{ color: "#6366f1" }}>
                    {method.value}
                  </p>
                  <p className="text-xs" style={{ color: "#94a3b8" }}>
                    {method.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="px-6 py-12">
          <div className="max-w-2xl mx-auto">
            <div
              className="p-8 rounded-2xl"
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h2
                className="text-xl font-semibold mb-6 text-center"
                style={{ color: "#1e293b" }}
              >
                Send us a message
              </h2>

              {isSubmitted ? (
                <div className="text-center py-8">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: "rgba(34, 197, 94, 0.1)" }}
                  >
                    <CheckCircle
                      className="w-8 h-8"
                      style={{ color: "#22c55e" }}
                    />
                  </div>
                  <h3
                    className="text-lg font-semibold mb-2"
                    style={{ color: "#1e293b" }}
                  >
                    Message Sent!
                  </h3>
                  <p style={{ color: "#64748b" }}>
                    Thank you for reaching out. We'll get back to you within 24
                    hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div
                      className="p-4 rounded-xl text-sm"
                      style={{
                        background: "rgba(239, 68, 68, 0.1)",
                        color: "#dc2626",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                      }}
                    >
                      {error}
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: "#1e293b" }}
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="w-full px-4 py-3 rounded-xl outline-none transition-all"
                        style={{
                          border: "1px solid #e2e8f0",
                          color: "#1e293b",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#6366f1")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: "#1e293b" }}
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full px-4 py-3 rounded-xl outline-none transition-all"
                        style={{
                          border: "1px solid #e2e8f0",
                          color: "#1e293b",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#6366f1")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: "#1e293b" }}
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-xl outline-none transition-all"
                      style={{
                        border: "1px solid #e2e8f0",
                        color: "#1e293b",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                      placeholder="How can we help?"
                    />
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: "#1e293b" }}
                    >
                      Message
                    </label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-xl outline-none transition-all resize-none"
                      style={{
                        border: "1px solid #e2e8f0",
                        color: "#1e293b",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                      onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                      placeholder="Tell us more about your needs..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-medium transition-all hover:scale-[1.02] disabled:opacity-50"
                    style={{
                      background:
                        "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
                    }}
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="px-6 py-8"
          style={{ borderTop: "1px solid #e2e8f0" }}
        >
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm" style={{ color: "#94a3b8" }}>
              © {new Date().getFullYear()} Botibonga. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/terms"
                className="text-sm transition-colors"
                style={{ color: "#94a3b8" }}
                onMouseEnter={(e) => (e.target.style.color = "#6366f1")}
                onMouseLeave={(e) => (e.target.style.color = "#94a3b8")}
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="text-sm transition-colors"
                style={{ color: "#94a3b8" }}
                onMouseEnter={(e) => (e.target.style.color = "#6366f1")}
                onMouseLeave={(e) => (e.target.style.color = "#94a3b8")}
              >
                Privacy
              </Link>
              <Link
                to="/about"
                className="text-sm transition-colors"
                style={{ color: "#94a3b8" }}
                onMouseEnter={(e) => (e.target.style.color = "#6366f1")}
                onMouseLeave={(e) => (e.target.style.color = "#94a3b8")}
              >
                About
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
